import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import type { IShoppingItem, IShoppingProduct, ITheme } from '@inter-ikea-kompis/types';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { ButtonSizeEnum } from '@inter-ikea-kompis/component-button';
import type { IMiniPriceDexfSettings } from '@inter-ikea-kompis/component-mini-price';
import { MiniPriceAlignmentEnum } from '@inter-ikea-kompis/component-mini-price';
import { MiniPrice } from '@inter-ikea-kompis/component-mini-price';

import type { ButtonVariant } from '@ingka/button-webc';
import {
	Button as SkapaButton,
	IconButton as SkapaIconButton
} from '@ingka/button-webc/passive.js';
import {
	arrowRight,
	checkmark,
	chevronDown,
	informationCircle,
	orderedList,
	registerIcon,
	shoppingBagAdd
} from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';

import MiniConfigurationSummaryIconTypeEnum from '../../enums/MiniConfigurationSummaryIconTypeEnum.js';
import type MiniConfigurationSummaryModalEnum from '../../enums/MiniConfigurationSummaryModalEnum.js';
import MiniConfigurationSummaryPrimaryButtonTypeEnum from '../../enums/MiniConfigurationSummaryPrimaryButtonTypeEnum.js';
import MiniConfigurationSummaryWhatsIncludedIconEnum from '../../enums/MiniConfigurationSummaryWhatsIncludedIconEnum.js';
import type IMiniConfigurationSummaryExperimental from '../../types/IMiniConfigurationSummaryExperimental.js';
import type IMiniConfigurationSummaryLocalizedInformation from '../../types/IMiniConfigurationSummaryLocalizedInformation.js';
import type IMiniConfigurationSummaryTranslations from '../../types/IMiniConfigurationSummaryTranslations.js';
import MiniConfigurationSummaryStyle from './MiniConfigurationSummaryStyle.js';

/**
 * The Mini Configuration Summary component combines Price with What's included
 * and a Next button. This is typically used in the header in a planner in the
 * configuration step(s).
 *
 * @event modalOpen Triggered when the user clicks on the eco fee link
 * @event modalClose Triggered when the user clicks on the sheet close button.
 * @event openWhatsIncluded Triggered when the user clicks on the whatsIncluded
 * link or whatsIncluded icon.
 * @event next When next button is clicked.
 */

export default class MiniConfigurationSummary extends KompisElement {
	// Accepts a object format corresponding to { ShoppingProduct } from '@inter-ikea-kompis/types'
	@property({ type: Object })
	public shoppingProducts!: IShoppingProduct[];

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: IMiniConfigurationSummaryLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IMiniConfigurationSummaryTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IMiniPriceDexfSettings | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Set price to be animated. The price should animate only when the total price has changed, e.g. when adding/removing products to the scene or returning from the configurator mode and price changes has occured in the configurator. If the price has not changed the price should not animate, e.g. when loading the planning step from a design.
	@property({ type: Boolean })
	public animatedPrice = false;

	// Disables button
	@property({ type: Boolean })
	public disabled = false;

	// Icon type
	@property({ type: String })
	public iconType: MiniConfigurationSummaryIconTypeEnum =
		MiniConfigurationSummaryIconTypeEnum.primaryInverted;

	// Hide whatsIncluded icon
	@property({ type: Boolean })
	public hideWhatsIncludedIcon = false;

	// Button size.
	@property({ type: String })
	public buttonSize: ButtonSizeEnum = ButtonSizeEnum.medium;

	// Button label
	@property({ type: String })
	public buttonLabel: string | null = null;

	// Hide button icon
	@property({ type: Boolean })
	public hideButtonIcon = false;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Renders the loading style
	@property({ type: Boolean })
	public loading = false;

	// Show a modal.
	@property({ type: String })
	public visibleModal: MiniConfigurationSummaryModalEnum | null = null;

	// Experimental options. NB: Use at your own discretion. Are considered non-breaking if altered or removed!
	@property({ type: Object })
	public experimental: IMiniConfigurationSummaryExperimental | null = null;

	private breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate());

	/**
	 * Renders view.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.shoppingProducts || !this.hasTranslationsAndSettings()) {
			return null;
		}

		return html`
			<style>
				${MiniConfigurationSummaryStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div class="container">
				${this.getPrice()}
				<div class="divider"></div>
				${this.getNextButton()}
			</div>
		`;
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.breakpointObserver.disconnect();
	}

	public override async connectedCallback(): Promise<void> {
		registerIcon(arrowRight);
		registerIcon(informationCircle);
		registerIcon(chevronDown);
		registerIcon(orderedList);
		registerIcon(shoppingBagAdd);
		registerIcon(checkmark);

		this.customElements.define('skapa-button', SkapaButton);
		this.customElements.define('skapa-icon-button', SkapaIconButton);
		this.customElements.define('skapa-icon', SkapaIcon);
		this.customElements.define('kompis-mini-price', MiniPrice);

		super.connectedCallback();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('theme')) {
			this.breakpointObserver.observe(this.theme);
		}
		return true;
	}

	private hasTranslationsAndSettings(): boolean {
		return (!!this.translations && !!this.settings) || !!this.localizedInformation;
	}

	private getTranslations(): IMiniConfigurationSummaryTranslations {
		return this.translations
			? this.translations
			: <IMiniConfigurationSummaryTranslations>this.localizedInformation?.translations;
	}

	/**
	 * Returns mini price.
	 *
	 * @returns Html.
	 */
	private getPrice(): RenderResult {
		return html`
			<kompis-mini-price
				.theme="${this.theme}"
				.disableLinks="${this.disableLinks}"
				.shoppingProducts="${this.shoppingProducts}"
				.localizedInformation="${this.localizedInformation}"
				.settings="${this.settings}"
				.translations="${this.translations}"
				.animatedPrice="${this.animatedPrice}"
				.align="${MiniPriceAlignmentEnum.right}"
				.showEcoFee="${true}"
				.visibleModal="${this.visibleModal}"
				@modalOpen="${(event: CustomEvent) =>
					this.dispatchEvent(
						new CustomEvent('modalOpen', { bubbles: true, detail: event.detail })
					)}"
				@modalClose="${(event: CustomEvent) =>
					this.dispatchEvent(
						new CustomEvent('modalClose', { bubbles: true, detail: event.detail })
					)}"
			>
				${this.getWhatsIncludedIcon()}
			</kompis-mini-price>
		`;
	}

	/**
	 * Returns the label for the checkout button.
	 *
	 * @returns Label to checkout button.
	 */
	private getNextButtonLabel(): string {
		const experiementalPrimaryButtonType = this.experimental?.primaryButtonType;
		switch (experiementalPrimaryButtonType) {
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.applyChanges:
				return this.getTranslations().applyChanges || '';
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.addToBag:
				return this.getTranslations().addToShoppingBagButton || '';
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.next:
			default:
				return this.getTranslations().buttonNextStep || '';
		}
	}

	private getIconType() {
		const experiementalPrimaryButtonType = this.experimental?.primaryButtonType;
		switch (experiementalPrimaryButtonType) {
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.applyChanges:
				return 'checkmark';
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.addToBag:
				return 'shopping-bag-add';
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.next:
			default:
				return 'arrow-right';
		}
	}

	private getButtonVariant() {
		const experiementalPrimaryButtonType = this.experimental?.primaryButtonType;
		const emphasisedVariant: ButtonVariant = 'emphasised';
		const primaryVariant: ButtonVariant = 'primary';

		switch (experiementalPrimaryButtonType) {
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.applyChanges:
				return primaryVariant;
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.addToBag:
			case MiniConfigurationSummaryPrimaryButtonTypeEnum.next:
			default:
				return emphasisedVariant;
		}
	}

	/**
	 * Returns the checkout button.
	 *
	 * @returns Button to checkout.
	 */
	private getNextButton(): RenderResult {
		const breakpointIndex = this.breakpointObserver.getBreakpointIndex();

		if (breakpointIndex !== null && breakpointIndex <= ThemeBreakpointIndexEnum.small) {
			return html`
				<skapa-icon-button
					variant="${this.getButtonVariant()}"
					size="${this.buttonSize === ButtonSizeEnum.small ? 'small' : 'medium'}"
					?disabled="${this.disabled}"
					?loading="${this.loading}"
					loadingSrText="${this.getTranslations().loading}"
					@click="${() => this.dispatchEvent(new CustomEvent('next', { bubbles: true }))}"
				>
					<skapa-icon slot="icon" flip-rtl icon="${this.getIconType()}"></skapa-icon>
					${this.getNextButtonLabel()}
				</skapa-icon-button>
			`;
		}

		return html`
			<skapa-button
				variant="${this.getButtonVariant()}"
				size="${this.buttonSize === ButtonSizeEnum.small ? 'small' : 'medium'}"
				?disabled="${this.disabled}"
				?loading="${this.loading}"
				loadingSrText="${this.getTranslations().loading}"
				icon-position="${this.experimental?.primaryButtonType ===
				MiniConfigurationSummaryPrimaryButtonTypeEnum.addToBag
					? 'leading'
					: 'trailing'}"
				@click="${() => this.dispatchEvent(new CustomEvent('next', { bubbles: true }))}"
			>
				${this.hideButtonIcon ||
				this.experimental?.primaryButtonType ===
					MiniConfigurationSummaryPrimaryButtonTypeEnum.applyChanges
					? null
					: html`<skapa-icon
							slot="icon"
							icon="${this.getIconType()}"
							?flip-rtl="${this.hideButtonIcon ? false : true}"
						></skapa-icon>`}
				${this.getNextButtonLabel()}
			</skapa-button>
		`;
	}

	/**
	 * @param shoppingProducts ShoppingProducts.
	 * @returns ShoppingItems.
	 */
	private getShoppingItems(shoppingProducts: IShoppingProduct[]): IShoppingItem[] {
		const validShoppingProducts = shoppingProducts.filter(
			(shoppingProduct) => !!shoppingProduct.product.content
		);

		return ProductUtility.getShoppingItems(validShoppingProducts);
	}

	/**
	 * Callback for onOpenWhatsIncluded event.
	 */
	private onOpenWhatsIncluded(): void {
		if (!this.disabled) {
			this.dispatchEvent(
				new CustomEvent('openWhatsIncluded', {
					bubbles: true,
					detail: {
						shoppingItems: this.getShoppingItems(this.shoppingProducts),
						triggerElement: this.queryWhatsIncludedButton()
					}
				})
			);
		}
	}

	private queryWhatsIncludedButton(): HTMLElement | null {
		return (
			this.shadowRoot?.querySelector('#whatsIncluded')?.shadowRoot?.querySelector('button') ?? null
		);
	}

	/**
	 * @returns WhatsIncludedIcon.
	 */
	private getWhatsIncludedIcon(): RenderResult {
		if (this.hideWhatsIncludedIcon) {
			return null;
		}

		const buttonVariant: ButtonVariant =
			this.iconType === MiniConfigurationSummaryIconTypeEnum.primaryInverted
				? 'primary'
				: 'tertiary';

		return html`
			<skapa-icon-button
				id="whatsIncluded"
				?disabled="${this.disabled}"
				variant="${buttonVariant}"
				?inverse="${this.iconType === MiniConfigurationSummaryIconTypeEnum.primaryInverted}"
				size="xsmall"
				@click="${!this.disabled ? this.onOpenWhatsIncluded.bind(this) : null}"
			>
				<skapa-icon
					slot="icon"
					icon="${this.experimental?.whatsIncludedIcon ||
					MiniConfigurationSummaryWhatsIncludedIconEnum.informationCircle}"
				></skapa-icon>
				${this.getTranslations().miniConfigurationSummaryAriaLabelWhatsIncluded}
			</skapa-icon-button>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-button': SkapaButton;
		'skapa-icon-button': SkapaIconButton;
		'skapa-icon': SkapaIcon;
		'kompis-mini-configuration-summary': MiniConfigurationSummary;
	}
}
